import React, { useEffect } from 'react';
import {
  ProductInformationProps,
  StyledTextField,
} from '../productInformation';
import {
  Box,
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { useQuery } from 'react-query';
import { client } from 'src/utils/api-client';
import { useTranslation } from 'react-i18next';
import Autocomplete from 'src/components/Autocomplete';
import { useLcaOrganization } from 'src/common/hooks';
import { useLcaOrganizationSettings } from 'src/Lca/hooks';
import {
  getProductComparableFieldValue,
  useProductFieldError,
} from '../validation';
import { buildSelectInputWithInfo } from '../fieldInfo';
import {
  ComplianceField,
  REGULATORY_FRAMEWORK_OPTIONS,
  parseRegulatoryFrameworks,
  serializeRegulatoryFrameworks,
} from 'src/Lca/compliance';

interface LcaIndustryCode {
  code: string;
  description: string;
}

type ProducerValues = {
  name: string;
  address: string;
  website: string;
};

const parseProducer = (rawValue: string): ProducerValues => {
  const fallback: ProducerValues = {
    name: '',
    address: '',
    website: '',
  };
  if (!rawValue) {
    return fallback;
  }

  const sections = rawValue
    .split('|')
    .map((section) => section.trim())
    .filter(Boolean);

  for (const section of sections) {
    const [left, ...rightParts] = section.split(':');
    const right = rightParts.join(':').trim();
    const key = left.toLowerCase().trim();

    if (key.includes('name') || key.includes('producer')) {
      fallback.name = right;
    } else if (key.includes('address')) {
      fallback.address = right;
    } else if (key.includes('website')) {
      fallback.website = right;
    }
  }

  if (!fallback.name && !fallback.address && !fallback.website) {
    fallback.name = rawValue;
  }

  return fallback;
};

const toProducer = (values: ProducerValues) =>
  [
    values.name && `Name: ${values.name}`,
    values.address && `Address: ${values.address}`,
    values.website && `Website: ${values.website}`,
  ]
    .filter(Boolean)
    .join(' | ');

export default function BasicInfo({
  setValues,
  values,
  basicInfoOnly,
}: ProductInformationProps) {
  const { t, i18n } = useTranslation('lca', { keyPrefix: 'basicInfo' });
  const regulatoryFrameworksQuestion = t('regulatoryFrameworksQuestion');

  const organization = useLcaOrganization();
  const { settings: lcaSettings } = useLcaOrganizationSettings(organization.id);
  const { data: cpcData } = useQuery('getCpc', () => {
    return client.get<LcaIndustryCode[]>('/web/industries/cpc');
  });
  const { data: naceData } = useQuery('getNace', () => {
    return client.get<LcaIndustryCode[]>('/web/industries/nace');
  });
  const isNewProduct = values.id === 0;
  const showNewProductFields = isNewProduct || basicInfoOnly;
  const defaultEcoinventVersion = lcaSettings?.default_ecoinvent_version;
  // versions with data loaded, plus the version of an existing product
  const ecoinventVersions = [
    ...(lcaSettings?.available_ecoinvent_versions ?? []),
  ];
  if (
    values.ecoinvent_version &&
    !ecoinventVersions.includes(values.ecoinvent_version)
  ) {
    ecoinventVersions.push(values.ecoinvent_version);
  }

  // a new product starts on the organization's default ecoinvent version
  useEffect(() => {
    if (isNewProduct && defaultEcoinventVersion) {
      setValues({
        ...values,
        ecoinvent_version: defaultEcoinventVersion,
      });
    }
  }, [defaultEcoinventVersion, values.id, setValues]);

  const handleName = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues({ ...values, name: e.target.value });
  };
  const handleDesc = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues({ ...values, description: e.target.value });
  };
  const handleEcoinventVersion = (e: SelectChangeEvent) => {
    setValues({ ...values, ecoinvent_version: e.target.value });
  };
  const handleProductCode = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues({ ...values, gtin_code: e.target.value });
  };
  const handleImporterName = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues({ ...values, importer_name: e.target.value });
  };
  const handleCpcIndustry = (value: string | null) => {
    setValues({
      ...values,
      cpc_industry: { code: value || '', description: '' },
    });
  };
  const handleNaceIndustry = (value: string | null) => {
    const naceOptional = values.nace_industries.slice(1);
    setValues({
      ...values,
      nace_industries: [
        { code: value || '', description: '' },
        ...naceOptional,
      ],
    });
  };

  const parsedProducer = parseProducer(values.manufacturer_name || '');
  const selectedRegulatoryFrameworks = parseRegulatoryFrameworks(
    values.regulatory_frameworks || ''
  );
  const nameError = useProductFieldError('name', values.name);
  const descriptionError = useProductFieldError(
    'description',
    values.description
  );
  const ecoinventVersionError = useProductFieldError(
    'ecoinvent_version',
    getProductComparableFieldValue(values as any, 'ecoinvent_version')
  );
  const cpcIndustryError = useProductFieldError(
    'cpc_industry',
    getProductComparableFieldValue(values as any, 'cpc_industry')
  );
  const naceIndustriesError = useProductFieldError(
    'nace_industries',
    getProductComparableFieldValue(values as any, 'nace_industries')
  );

  const updateProducer = (patch: Partial<ProducerValues>) => {
    const merged = {
      ...parsedProducer,
      ...patch,
    };
    setValues({
      ...values,
      manufacturer_name: toProducer(merged),
    });
  };
  const handleRegulatoryFrameworks = (e: SelectChangeEvent<string[]>) => {
    const { value } = e.target;
    const frameworks = typeof value === 'string' ? value.split(',') : value;
    setValues({
      ...values,
      regulatory_frameworks: serializeRegulatoryFrameworks(
        frameworks as Array<
          'epd' | 'dppEspr' | 'scope3Upstream' | 'productCarbonFootprint'
        >
      ),
    });
  };

  return (
    <div
      style={{
        display: 'flex',
        gap: '24px',
        flexDirection: 'column',
      }}
    >
      <ComplianceField
        excelRow={3}
        regulatoryFrameworks={values.regulatory_frameworks}
      >
        <StyledTextField
          fullWidth
          label={t('productName')}
          name="name"
          value={values.name}
          onChange={handleName}
          required
          error={!!nameError}
          helperText={nameError}
        />
      </ComplianceField>
      <ComplianceField
        excelRow={4}
        regulatoryFrameworks={values.regulatory_frameworks}
      >
        <StyledTextField
          fullWidth
          label={t('productDescription')}
          name="description"
          value={values.description}
          onChange={handleDesc}
          multiline
          rows={3}
          required
          error={!!descriptionError}
          helperText={descriptionError}
        />
      </ComplianceField>

      <FormControl fullWidth error={!!ecoinventVersionError}>
        <InputLabel>{t('ecoinventVersion')}</InputLabel>
        <Select
          value={values.ecoinvent_version ?? ''}
          label={t('ecoinventVersion')}
          input={buildSelectInputWithInfo({
            label: t('ecoinventVersion'),
            title: i18n.t('lca:fieldInfoFallback', {
              field: t('ecoinventVersion'),
            }),
          })}
          onChange={handleEcoinventVersion}
          disabled={!isNewProduct}
        >
          {ecoinventVersions.map((version) => (
            <MenuItem key={version} value={version}>
              {version}
            </MenuItem>
          ))}
        </Select>
        {ecoinventVersionError && (
          <FormHelperText>{ecoinventVersionError}</FormHelperText>
        )}
        {!ecoinventVersionError && isNewProduct && (
          <FormHelperText>{t('ecoinventVersionHelper')}</FormHelperText>
        )}
      </FormControl>

      {showNewProductFields && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <FormControl fullWidth>
            <Autocomplete
              label={t('productCategory')}
              placeholder={t('productCategory')}
              showInfoIcon
              infoText={t('codes.CPC')}
              options={cpcData?.data.map((option) => ({
                value: option.code,
                label: `${option.code} ${option.description}`,
              }))}
              value={values.cpc_industry?.code ?? ''}
              onChange={(value: string | null) => handleCpcIndustry(value)}
              errorText={cpcIndustryError}
            />
          </FormControl>

          <ComplianceField
            excelRow={8}
            regulatoryFrameworks={values.regulatory_frameworks}
          >
            <FormControl fullWidth>
              <Autocomplete
                label={t('industryNaceCode')}
                placeholder={t('industryNaceCode')}
                showInfoIcon
                infoText={t('codes.NACE')}
                options={naceData?.data.map((option) => ({
                  value: option.code,
                  label: `${option.code} ${option.description}`,
                }))}
                value={values.nace_industries[0]?.code ?? ''}
                onChange={(value: string | null) => handleNaceIndustry(value)}
                errorText={naceIndustriesError}
              />
            </FormControl>
          </ComplianceField>
          <FormControl fullWidth>
            <InputLabel id="regulatory-frameworks-label">
              {regulatoryFrameworksQuestion}
            </InputLabel>
            <Select
              labelId="regulatory-frameworks-label"
              multiple
              value={selectedRegulatoryFrameworks}
              label={regulatoryFrameworksQuestion}
              input={buildSelectInputWithInfo({
                label: regulatoryFrameworksQuestion,
                title: i18n.t('lca:fieldInfoFallback', {
                  field: regulatoryFrameworksQuestion,
                }),
              })}
              onChange={handleRegulatoryFrameworks}
              renderValue={(selected) =>
                (selected as string[])
                  .map((selectedValue) =>
                    t(
                      `regulatoryFrameworksOptions.${selectedValue}.label`,
                      selectedValue
                    )
                  )
                  .join(', ')
              }
            >
              {REGULATORY_FRAMEWORK_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.id}>
                  <Checkbox
                    checked={selectedRegulatoryFrameworks.includes(option.id)}
                  />
                  <ListItemText
                    primary={t(
                      `regulatoryFrameworksOptions.${option.id}.label`
                    )}
                    secondary={t(
                      `regulatoryFrameworksOptions.${option.id}.description`
                    )}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <ComplianceField
            excelRow={17}
            regulatoryFrameworks={values.regulatory_frameworks}
          >
            <StyledTextField
              fullWidth
              label={t('productCode')}
              name="product-code"
              value={values.gtin_code}
              onChange={handleProductCode}
            />
          </ComplianceField>
          <ComplianceField
            excelRow={10}
            regulatoryFrameworks={values.regulatory_frameworks}
          >
            <StyledTextField
              fullWidth
              label={t('producerName')}
              name="producer-name"
              value={parsedProducer.name}
              onChange={(e) => updateProducer({ name: e.target.value })}
            />
          </ComplianceField>
          <ComplianceField
            excelRow={11}
            regulatoryFrameworks={values.regulatory_frameworks}
          >
            <StyledTextField
              fullWidth
              label={t('producerAddress')}
              name="producer-address"
              value={parsedProducer.address}
              onChange={(e) => updateProducer({ address: e.target.value })}
            />
          </ComplianceField>
          <ComplianceField
            excelRow={12}
            regulatoryFrameworks={values.regulatory_frameworks}
          >
            <StyledTextField
              fullWidth
              label={t('producerWebsite')}
              name="producer-website"
              value={parsedProducer.website}
              onChange={(e) => updateProducer({ website: e.target.value })}
            />
          </ComplianceField>
          <StyledTextField
            fullWidth
            label={t('importerName')}
            name="importer-name"
            value={values.importer_name}
            onChange={handleImporterName}
          />
        </Box>
      )}
    </div>
  );
}
